/**
 * Compile-and-fix agent: compiles DSL code; on errors, sends them to the AI and retries.
 * Used by Script Editor and Script Generator to get fix suggestions from the model.
 */

import { scriptsApi } from '@/services/api/scripts';
import type { CompileScriptResponse } from '@/types/script';
import { generateSyntaxCorrectionPrompt } from './ai-prompts';

const CORRECTION_SYSTEM_PROMPT = `You are an expert QuantQuack DSL developer specializing in fixing compilation errors.

Your prompt includes: (1) the code and compilation errors, (2) the full DSL syntax reference, (3) a "DSL FIX AGENT MEMORY" section with error→fix table, Pine pattern→built-in table, and antipatterns. Use that memory: match each error message to the fix (e.g. "Circular dependency" → use count_consecutive/accumulate_while/flag_once/latch/etc.; "input must be literal" → use literal only; "Type mismatch" → check function signatures and types). Replace self-reference patterns with the indicated built-ins.

Output ONLY the corrected DSL code: no markdown, no explanations, no code blocks. Raw DSL code only.`;

function extractCodeFromAIResponse(response: string): string {
  let code = response.trim();
  if (code.startsWith('```')) {
    code = code.replace(/^```[\w]*\n?/, '').replace(/\n?```$/, '');
  }
  return code.trim();
}

export interface CompileAndFixResult {
  code: string;
  success: boolean;
  errors?: CompileScriptResponse['errors'];
  rounds: number;
}

export type CallAIFn = (
  prompt: string,
  systemPrompt: string,
  options?: { temperature?: number; maxTokens?: number }
) => Promise<string | null>;

/** Default max code length (chars) to avoid oversized prompts. */
const DEFAULT_MAX_CODE_LENGTH = 80_000;

/**
 * Compile DSL code; if there are errors, ask the AI to fix them and recompile (up to maxRetries).
 * Optionally pass previousRoundErrors so the model avoids repeating a fix that introduced new errors.
 */
export async function compileAndFix(
  dslCode: string,
  callAI: CallAIFn,
  options?: { maxRetries?: number; maxCodeLength?: number }
): Promise<CompileAndFixResult> {
  const maxRetries = options?.maxRetries ?? 2;
  const maxCodeLength = options?.maxCodeLength ?? DEFAULT_MAX_CODE_LENGTH;
  let code = dslCode.trim();

  if (code.length > maxCodeLength) {
    return {
      code,
      success: false,
      errors: [{ message: `Script too long for auto-fix (${code.length} chars, max ${maxCodeLength}). Shorten or fix manually.`, type: 'size_limit' }],
      rounds: 0,
    };
  }

  let round = 0;
  let previousRoundErrors: NonNullable<CompileScriptResponse['errors']> = [];

  while (round <= maxRetries) {
    const compilation: CompileScriptResponse = await scriptsApi.compileCode(code);

    if (compilation.success) {
      return { code, success: true, rounds: round };
    }

    const rawErrors = compilation.errors ?? [];
    const errors: NonNullable<CompileScriptResponse['errors']> = rawErrors.map((e) => ({
      ...e,
      type: e.type ?? 'error',
    }));
    if (errors.length === 0) {
      return { code, success: false, errors: [], rounds: round };
    }

    if (round >= maxRetries) {
      return { code, success: false, errors, rounds: round };
    }

    const prompt = generateSyntaxCorrectionPrompt({
      originalCode: code,
      compilationErrors: errors.map((e) => ({
        line: e.line,
        column: e.column,
        message: e.message,
        type: e.type,
      })),
      previousRoundErrors: previousRoundErrors.length > 0 ? previousRoundErrors : undefined,
      userRequest: 'Fix all compilation errors.',
    });

    const corrected = await callAI(prompt, CORRECTION_SYSTEM_PROMPT, {
      temperature: 0.2,
      maxTokens: 4000,
    });

    if (!corrected) {
      return { code, success: false, errors, rounds: round };
    }

    previousRoundErrors = errors;
    code = extractCodeFromAIResponse(corrected);
    round += 1;
  }

  return { code, success: false, errors: previousRoundErrors, rounds: round };
}
